import React, { lazy, Suspense, useEffect, useState } from 'react';
import HeroCarousel from '../components/HeroCarousel';
import Categories from '../components/Categories';
import BrandsBar from '../components/BrandsBar';
import VitaminTestTeaser from '../components/VitaminTestTeaser';
import ProductCarousel from '../components/ProductCarousel';
import DeferredSection from '../components/DeferredSection';

import { useSeo } from '../lib/useSeo';

const Pillars = lazy(() => import('../components/Pillars'));
const BlogTeaser = lazy(() => import('../components/BlogTeaser'));
const HomeCatalog = lazy(() => import('../components/HomeCatalog'));

const byHomeOrder = (orderKey) => (a, b) => {
  const left = Number.isFinite(Number(a?.[orderKey])) ? Number(a[orderKey]) : Number.MAX_SAFE_INTEGER;
  const right = Number.isFinite(Number(b?.[orderKey])) ? Number(b[orderKey]) : Number.MAX_SAFE_INTEGER;
  if (left !== right) return left - right;
  return (a?.name || '').localeCompare(b?.name || '', 'uk');
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useSeo({
    title: 'Вітаміни та Хелатні Мінерали — Доставка по Україні',
    description: 'Купуйте якісні вітаміни та хелатні мінерали від MSA Sport. AM/PM мультивітамінна система для щоденного здоровʼя та активного способу життя. Доставка Новою Поштою по всій Україні.',
    keywords: 'вітаміни, хелатні мінерали, БАД, мультивітамін, MSA Sport, здоровʼя, активний спосіб життя, Україна',
  });

  useEffect(() => {
    let cancelled = false;

    // slim=1 drops description/composition/seo* — fields no card on this page
    // renders. The home flags are preserved server-side for home=1 requests.
    fetch('/api/products?home=1&slim=1')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch failed'))))
      .then((data) => {
        if (!cancelled) {
          setProducts(Array.isArray(data) ? data : []);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Only show products explicitly marked in admin — no automatic fallback.
  const recommended = products
    .filter((p) => p.showOnHomeFeatured)
    .sort(byHomeOrder('homeFeaturedOrder'));

  return (
    <div>
      <div className="pt-4 sm:pt-6">
        <HeroCarousel />
      </div>
      <BrandsBar />
      <Categories />
      <VitaminTestTeaser />
      {recommended.length > 0 ? (
        <ProductCarousel eyebrow="Рекомендуємо" title="Хіти вибору клієнтів" products={recommended} />
      ) : (
        // The carousel only exists once /api/products resolves. Without a
        // placeholder it pushed every section below it down on arrival, which is
        // most of this page's CLS. 541px is measured from the live site at 375px
        // wide; no reservation above sm: so desktop layout is unchanged.
        !loaded && <div aria-hidden className="h-[541px] sm:hidden" />
      )}
      <DeferredSection minHeight={600}>
        <Suspense fallback={null}>
          <HomeCatalog />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={420}>
        <Suspense fallback={null}>
          <Pillars />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={420}>
        <BlogTeaserWrap />
      </DeferredSection>
      <SeoBlock />
    </div>
  );
};

const BlogTeaserWrap = () => (
  <Suspense fallback={null}>
    <BlogTeaser />
  </Suspense>
);

const SeoBlock = () => (
  <section className="py-14 sm:py-20">
    <div className="max-w-[920px] mx-auto px-4 sm:px-6">
      <h2 className="font-display text-[24px] sm:text-[32px] font-bold leading-tight">MSA — біологічно активні добавки для сучасного життя</h2>
      <p className="text-[var(--msa-ink-soft)] mt-4 leading-relaxed">MSA — це український бренд біологічно активних добавок для людей, які живуть у швидкому ритмі, стежать за своїм здоровʼям та хочуть стабільної енергії та якісного відновлення.</p>
      <p className="text-[var(--msa-ink-soft)] mt-3 leading-relaxed">Наш флагман — MSA Multivitamin + Chelated Minerals — це основа системи з AM/PM розділенням та хелатними мінералами для максимальної біодоступності.</p>
      <h3 className="font-display text-xl font-bold mt-8 mb-3">Важливо</h3>
      <p className="text-[var(--msa-ink-soft)] leading-relaxed">Продукти MSA є дієтичними добавками. Вони не є лікарськими засобами і не замінюють консультацію лікаря.</p>
    </div>
  </section>
);

export default HomePage;
